/** @jsx jsx */
import React, { useState, useRef, useCallback } from 'react';
import cn from 'classnames';
import { Query } from 'react-apollo';
import { jsx } from '@emotion/core';

import GridFlaskController from '../../controllers/GridFlaskController';
import GridFlask from '../../components/GridFlask';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import BlockingStatus from '../../components/BlockingStatus';
import Col from '../../components/Col';
import { REP_OPTIONS as RepFilterOptions } from '../../components/actions/RepFilterAction';
import ActionsOrganizerShipping from './components/ActionsOrganizerShipping'
import userSettingsQuery from '../../graphql/queries/userSettings.query';
import { getDefaultUserSettings, getUserSettings } from '../../utils'
import { isAccountRep } from '../../utils/isAccountRep';
import ArrayUtils from '../../utils/arrayUtils'

import formatters from '../invoices/formatters'
import { MODULES_NAMES } from '../modulesList'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage: boolean,
  showGroupPanel?: boolean,
  active: boolean,
};

const Shipping = (props: Props) => {
  const moduleName = MODULES_NAMES.SHIPPING;

  const {
    isFullPage = true,
    showGroupPanel = false,
    active
  } = props;

  const menuRef = useRef();
  const menuButtonRef = useRef();

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  
  // State to track the displayed view name for UI updates
  // - undefined: initial state, will sync from query on first load
  // - null: user selected "Default View"
  // - string: user selected a named view
  const [displayedViewName, setDisplayedViewName] = useState(undefined);

  const changeDisplayMenu = function(status) {
    setDisplayMenu(status);
  };

  const handler = useCallback(function(e) {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(function(value) {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(handler, active);

  /**
   * Custom aggregation function for 9L and Gallons columns.
   * Applies ROUNDUP (Math.ceil) to aggregated values for both
   * grouped rows and the footer total row.
   * - 9L: rounds up to whole number (0 decimal places)
   * - Gallons: rounds up to 2 decimal places
   */
  const shippingRoundUpAggregation = (grid, wjGrid, wjcCore) => {
    return grid.formatItem.addHandler((s, e) => {
      // Handle grouped rows in cells panel
      if (e.panel === s.cells) {
        let r = s.rows[e.row]
        let c = s.columns[e.col]
        let item = s.rows[e.row].dataItem
        let group = r instanceof wjGrid.GroupRow ? item : null

        // Apply ROUNDUP to 9L column for grouped rows (0 decimal places)
        if (c.binding === '9L' && group) {
          let sum = group.getAggregate('Sum', '9L')
          let roundedUp = Math.ceil(sum)
          e.cell.textContent = wjcCore.Globalize.format(roundedUp, 'n0')
        }

        // Apply ROUNDUP to Gal column for grouped rows (2 decimal places)
        if (c.binding === 'Gal' && group) {
          let sum = group.getAggregate('Sum', 'Gal')
          let roundedUp = Math.ceil(sum * 100) / 100
          e.cell.textContent = wjcCore.Globalize.format(roundedUp, 'n2')
        }
      }

      // Handle footer (total row)
      if (e.panel === s.columnFooters) {
        let c = s.columns[e.col]

        // Apply ROUNDUP to 9L column footer (0 decimal places)
        if (c.binding === '9L') {
          let total = 0
          for (let val of s.collectionView.items) {
            total += val['9L'] || 0
          }
          let roundedUp = Math.ceil(total)
          e.cell.textContent = wjcCore.Globalize.format(roundedUp, 'n0')
        }

        // Apply ROUNDUP to Gal column footer (2 decimal places)
        if (c.binding === 'Gal') {
          let total = 0
          for (let val of s.collectionView.items) {
            total += val['Gal'] || 0
          }
          let roundedUp = Math.ceil(total * 100) / 100
          e.cell.textContent = wjcCore.Globalize.format(roundedUp, 'n2')
        }
      }
    })
  }

  return (
    <PageContainer
      className={cn({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
        'page--with-group-panel': showGroupPanel,
      })}
      isFullPage={isFullPage}
      active={props.active}
    >
      <Query
        query={userSettingsQuery}
        variables={{
          moduleName: moduleName,
        }}
      >
        {({ data: dataGrid, loading }: any) => {
          // we need to wait until the Query is loaded otherwise
          // we'll have to refetch the grid multiple times (first on
          // page load and then when this query is loaded)
          if (loading || !dataGrid)
            return (
              <BlockingStatus
                excludeHeader
                active
                container={document.body}
                text="Loading..."
              />
            );

          var userSettings = getUserSettings(dataGrid, {
            defaultFilters: function(settings) {
              if (settings && isAccountRep(settings.accountType)) {
                var defaultOption = RepFilterOptions[0];

                return {
                  Rep: {
                    [defaultOption.value]: {
                      eq: settings.repCode,
                    },
                  },
                };
              }

              return {};
            },
          });

          var defaultUserSettings = getDefaultUserSettings(dataGrid)
          
          // Get view name from query for initial sync
          var queryViewName = dataGrid && dataGrid.userSettings && dataGrid.userSettings.viewName
            ? dataGrid.userSettings.viewName
            : null;
          
          // Only sync displayedViewName from query on initial load (when displayedViewName is undefined)
          // After initial load, displayedViewName is managed entirely by user selection
          if (displayedViewName === undefined) {
            // Use setTimeout to avoid setState during render
            setTimeout(function() { setDisplayedViewName(queryViewName); }, 0);
          }
          
          // Determine what to show in the UI:
          // - If displayedViewName is undefined (initial load), use queryViewName
          // - Otherwise use displayedViewName (which could be null for Default View)
          var currentViewNameForUI = displayedViewName === undefined ? queryViewName : displayedViewName;

          // Handler for when a view is loaded - just updates the UI display name
          // The actual view settings are applied by onApplyViewSettings in ActionsHeader
          var handleViewLoaded = function(viewName) {
            setDisplayedViewName(viewName);
          };

          return (
            <GridFlaskController
              entity={moduleName}
              flattenFunction={ArrayUtils.noFlatteningCollection}
              loading={loading}
              defaultSorting={defaultUserSettings.defaultSorting}
              defaultColumns={defaultUserSettings.defaultColumns}
              defaultGroupPanels={defaultUserSettings.defaultGroupPanels}
              isMobile={isMobile}
              {...userSettings}
            >
              {({
                collection,
                columnsMapping,
                currentItem,
                defaultColumns,
                frozenColumns,
                grid,
                groups,
                hasAllColumnsHidden,
                onRowSelected,
                onGridInitialized,
                onGroupPanelChanged,
                onColSorted,
                onColResized,
                onColDragged,
                onColFreezed,
                onRowFreezed,
                onGroupCollapsedChanged,
                onClearSelection,
                onColumnsMappingChange,
                onCurrentItemChanged,
                onViewSaved,
                onInternalGridFiltersChanged,
                onViewReset,
                onApplyViewSettings,
                onExporting,
                onExported,
                hasDataToDisplay,
                refetch,
                isLoading,
                isCollapseLoading,
                setCollapseLoading,
                status,
                saveStatus,
                selectedItems,
                userViewState,
                playResetAnimation,
                handleResetAnimation,
                setFlexGridFilter,
                clearInternalFilters,
                toggleMultiSort,
                multiColumnSort,
                exportProgress,
                setExportProgress,
                getGridFilterDefinition,
              }) => {
                return (
                  <React.Fragment>
                    {/* Show loading indicator during collapse/expand operations */}
                    {isCollapseLoading && (
                      <BlockingStatus
                        excludeHeader
                        active
                        container={document.body}
                        text="Processing..."
                      />
                    )}
                    <ActionsHeader
                      grid={grid}
                      collection={collection}
                      defaultColumnsMapping={defaultColumns}
                      columnsMapping={columnsMapping}
                      selectedItems={selectedItems}
                      moduleName={moduleName}
                      groups={groups}
                      saveStatus={saveStatus}
                      status={status}
                      loading={isLoading || status !== 'loaded'}
                      onViewSaved={onViewSaved}
                      onViewReset={onViewReset}
                      onApplyViewSettings={onApplyViewSettings}
                      onColumnsMappingChange={onColumnsMappingChange}
                      onExporting={onExporting}
                      onExported={onExported}
                      userViewState={userViewState}
                      refetch={refetch}
                      hasAllColumnsHidden={hasAllColumnsHidden}
                      hasDataToDisplay={hasDataToDisplay}
                      frozenColumns={frozenColumns}
                      currentItem={currentItem}
                      defaultSettings={defaultUserSettings}
                      handleResetAnimation={handleResetAnimation}
                      toggleMultiSort={toggleMultiSort}
                      multiColumnSort={multiColumnSort}
                      setExportProgress={setExportProgress}
                      // Multiple saved views feature props
                      useMultipleViews={true}
                      currentViewName={currentViewNameForUI}
                      onViewLoaded={handleViewLoaded}
                      getGridFilterDefinition={getGridFilterDefinition}
                    >
                      {function(actions) {
                        var isOneRowSelected = actions.isOneRowSelected;
                        var ColumnsAction = actions.ColumnsAction;
                        return (
                          <React.Fragment>
                            <MobileView>
                              <ModuleMenuBar
                                {...actions}
                                moduleRefetch={refetch}
                                ColumnsAction={ColumnsAction}
                                changeDisplayMenu={changeDisplayMenu}
                                displayMenu={displayMenu}
                                showCollapse={true}
                                onGroupCollapsedChanged={onGroupCollapsedChanged}
                                menuButtonRef={menuButtonRef}
                                clearInternalFilters={clearInternalFilters}
                              />
                            </MobileView>
                            <div
                              ref={menuRef}
                              style={{
                                justifyContent: 'space-between',
                                padding: '0 10px',
                                display: 'flex',
                                flexDirection: 'row',
                                minHeight: 'auto',
                                ...(isMobile && {
                                  position: 'absolute',
                                  bottom: 0,
                                  boxShadow:
                                    '0px -4px 4px 0px #ACACAC40',
                                  zIndex: 100,
                                  background: '#fff',
                                  borderRadius: '8px 8px 0px 0px',
                                  width: '100%',
                                  display: isOneRowSelected || displayMenu
                                    ? 'flex'
                                    : 'none',
                                  justifyContent: 'center',
                                  height: (displayMenu && '189px') || (isOneRowSelected && '90px')
                                }),
                              }}
                            >
                              <Col>
                                <ActionsOrganizerShipping
                                  {...actions}
                                  minHeight="80px"
                                  isRep={isAccountRep(userSettings.accountType)}
                                  saveStatus={saveStatus}
                                  repCode={userSettings.repCode}
                                  onGroupCollapsedChanged={onGroupCollapsedChanged}
                                  clearInternalFilters={clearInternalFilters}
                                  displayRowMenu={isOneRowSelected}
                                  displayMenu={displayMenu}
                                  toggleMultiSort={toggleMultiSort}
                                  setCollapseLoading={setCollapseLoading}
                                />
                              </Col>
                            </div>
                          </React.Fragment>
                        )
                      }}
                    </ActionsHeader>
                    <div>
                      <GridFlask
                        collection={collection}
                        columnsMapping={columnsMapping}
                        id={moduleName}
                        grid={grid}
                        frozenColumns={frozenColumns}
                        hasAllColumnsHidden={hasAllColumnsHidden}
                        hasDataToDisplay={hasDataToDisplay}
                        onGridInitialized={onGridInitialized}
                        onInternalGridFiltersChanged={onInternalGridFiltersChanged}
                        onClearSelection={onClearSelection}
                        onRowSelected={onRowSelected}
                        onGroupPanelChanged={onGroupPanelChanged}
                        onCurrentItemChanged={onCurrentItemChanged}
                        onColSorted={onColSorted}
                        onColResized={onColResized}
                        onColDragged={onColDragged}
                        onColFreezed={onColFreezed}
                        onRowFreezed={onRowFreezed}
                        status={status}
                        formatters={formatters}
                        showFrozenBars
                        showGroupPanel
                        active={props.active}
                        groups={groups}
                        customAggregation={shippingRoundUpAggregation}
                        playResetAnimation={playResetAnimation}
                        setFlexGridFilter={setFlexGridFilter}
                        menuRef={menuRef}
                        isMobile={isMobile}
                        currentItem={currentItem}
                        multiColumnSort={multiColumnSort}
                        exportProgress={exportProgress}
                      />
                    </div>
                  </React.Fragment>
                );
              }}
            </GridFlaskController>
          );
        }}
      </Query>
    </PageContainer>
  );
}

export default Shipping;
